<template>
  <div class="dropdown-wrapper">
    <button
      type="button"
      class="dropdown-trigger"
      :class="{ open: isOpen, selected: !!selected }"
      @click="toggleDropdown"
    >
      <div class="selected-content">
        <span class="dropdown-label">
          Специализация
        </span>

        <span class="selected-value">
          {{ selected?.name || "Выберите специфику" }}
        </span>
      </div>

      <div class="arrow-box">
        <svg
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </button>

    <Transition name="dropdown">
      <ul
        v-if="isOpen"
        class="dropdown-list"
      >
        <li
          v-for="item in specifics"
          :key="item.id"
        >
          <button
            type="button"
            class="dropdown-item"
            :class="{ active: selected?.id === item.id }"
            @click.stop="selectSpecific(item)"
          >
            <div class="item-main">
              <span class="item-dot"></span>

              <span class="item-name">
                {{ item.name }}
              </span>
            </div>

            <span
              v-if="selected?.id === item.id"
              class="check"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M4.5 10.5L8 14L15.5 6.5"
                  stroke="currentColor"
                  stroke-width="1.9"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </button>
        </li>
      </ul>
    </Transition>
  </div>
</template>

<script setup>
import {
  ref,
  watch
} from "vue";

import SpecificsService
  from "../utils/SpecificsService";

const props = defineProps({
  modelValue: Object
});

const emit = defineEmits([
  "update:modelValue"
]);

const specifics = ref(
  SpecificsService.getSpecifics()
);

const selected = ref(
  props.modelValue || null
);

const isOpen = ref(false);

watch(
  () => props.modelValue,
  (newVal) => {
    selected.value = newVal;
  },
  {
    immediate: true
  }
);

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}

function selectSpecific(item) {
  selected.value = item;

  emit(
    "update:modelValue",
    item
  );

  isOpen.value = false;
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.dropdown-wrapper {
  position: relative;

  width: 100%;
  max-width: 700px;

  font-family:
    "Eastman-Regular",
    sans-serif;
}

/* =========================================================
   TRIGGER
========================================================= */

.dropdown-trigger {
  width: 100%;
  min-height: 58px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 14px;

  padding:
    9px
    10px
    9px
    16px;

  cursor: pointer;

  text-align: left;

  background: #f6f7f9;

  border:
    1px solid
    #dfe3e8;

  border-radius: 15px;

  box-shadow:
    inset 0 1px 2px
    rgba(
      23,
      34,
      52,
      0.035
    );

  transition:
    background-color
    0.2s ease,
    border-color
    0.2s ease,
    box-shadow
    0.2s ease,
    transform
    0.2s ease;
}

.dropdown-trigger:hover {
  background: #f9fafb;

  border-color:
    #d5dae2;
}

.dropdown-trigger.open {
  background: #ffffff;

  border-color:
    #5b8df2;

  box-shadow:
    0 0 0 3px
    rgba(
      83,
      139,
      242,
      0.12
    );
}

.dropdown-trigger.selected {
  border-color:
    rgba(
      61,
      130,
      237,
      0.22
    );

  background:
    linear-gradient(
      135deg,
      rgba(
        53,
        132,
        247,
        0.055
      ),
      rgba(
        83,
        104,
        232,
        0.035
      )
    );
}

/* =========================================================
   TEXT
========================================================= */

.selected-content {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 3px;
}

.dropdown-label {
  color: #949aa4;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing:
    0.07em;

  text-transform:
    uppercase;
}

.selected-value {
  overflow: hidden;

  color: #30353d;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 13px;

  white-space: nowrap;

  text-overflow:
    ellipsis;
}

/* =========================================================
   ARROW
========================================================= */

.arrow-box {
  width: 38px;
  height: 38px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #7f8792;

  background:
    #eceff3;

  border-radius: 11px;

  transition:
    color
    0.2s ease,
    background-color
    0.2s ease,
    transform
    0.25s ease;
}

.arrow-box svg {
  width: 16px;
  height: 16px;
}

.dropdown-trigger.open
.arrow-box {
  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3596f8,
      #5368e7
    );

  transform:
    rotate(180deg);

  box-shadow:
    0 6px 14px
    rgba(
      48,
      117,
      224,
      0.22
    );
}

/* =========================================================
   LIST
========================================================= */

.dropdown-list {
  position: absolute;

  top:
    calc(100% + 8px);

  left: 0;
  right: 0;

  z-index: 100;

  max-height: 280px;

  margin: 0;
  padding: 7px;

  overflow-y: auto;

  list-style: none;

  background: #ffffff;

  border:
    1px solid
    #e1e5ea;

  border-radius: 16px;

  box-shadow:
    0 18px 42px
    rgba(
      22,
      33,
      51,
      0.11
    );

  scrollbar-width: thin;
  scrollbar-color:
    rgba(
      105,
      113,
      127,
      0.25
    )
    transparent;
}

.dropdown-list::-webkit-scrollbar {
  width: 6px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: transparent;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background:
    rgba(
      105,
      113,
      127,
      0.25
    );

  border-radius: 999px;
}

/* =========================================================
   ITEM
========================================================= */

.dropdown-item {
  width: 100%;
  min-height: 46px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;

  padding:
    8px
    10px;

  cursor: pointer;

  text-align: left;

  background: transparent;

  border: none;
  border-radius: 11px;

  transition:
    background-color
    0.18s ease,
    transform
    0.18s ease;
}

.dropdown-item:hover {
  background:
    rgba(
      64,
      132,
      237,
      0.06
    );
}

.dropdown-item.active {
  background:
    linear-gradient(
      135deg,
      rgba(
        53,
        132,
        247,
        0.1
      ),
      rgba(
        83,
        104,
        232,
        0.055
      )
    );
}

/* =========================================================
   ITEM CONTENT
========================================================= */

.item-main {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 10px;
}

.item-dot {
  width: 8px;
  height: 8px;

  flex-shrink: 0;

  background: #d8dde5;

  border-radius: 50%;

  transition:
    background-color
    0.18s ease,
    box-shadow
    0.18s ease;
}

.dropdown-item:hover
.item-dot {
  background: #7ca9ee;
}

.dropdown-item.active
.item-dot {
  background: #4387ee;

  box-shadow:
    0 0 0 4px
    rgba(
      67,
      135,
      238,
      0.11
    );
}

.item-name {
  overflow: hidden;

  color: #454a52;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 12px;

  white-space: nowrap;

  text-overflow:
    ellipsis;
}

.dropdown-item.active
.item-name {
  color: #2d5fae;

  font-family:
    "Eastman-Medium",
    sans-serif;
}

/* =========================================================
   CHECK
========================================================= */

.check {
  width: 27px;
  height: 27px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3596f8,
      #5368e7
    );

  border-radius: 9px;

  box-shadow:
    0 5px 12px
    rgba(
      48,
      117,
      224,
      0.2
    );
}

.check svg {
  width: 14px;
  height: 14px;
}

/* =========================================================
   DARK
========================================================= */

html[data-theme="dark"]
.dropdown-trigger {
  background: #2a2b30;

  border-color:
    #414349;

  box-shadow:
    inset 0 1px 2px
    rgba(
      0,
      0,
      0,
      0.2
    );
}

html[data-theme="dark"]
.dropdown-trigger:hover {
  background: #2e2f34;
}

html[data-theme="dark"]
.dropdown-trigger.open {
  background: #303137;

  border-color:
    #5a8ff0;

  box-shadow:
    0 0 0 3px
    rgba(
      71,
      128,
      231,
      0.15
    );
}

html[data-theme="dark"]
.dropdown-trigger.selected {
  background:
    linear-gradient(
      135deg,
      rgba(
        53,
        132,
        247,
        0.1
      ),
      rgba(
        83,
        104,
        232,
        0.07
      )
    );

  border-color:
    rgba(
      75,
      138,
      238,
      0.28
    );
}

html[data-theme="dark"]
.dropdown-label {
  color: #777d86;
}

html[data-theme="dark"]
.selected-value {
  color: #f1f2f4;
}

html[data-theme="dark"]
.arrow-box {
  color: #9a9fa8;

  background: #36373d;
}

html[data-theme="dark"]
.dropdown-list {
  background: #25262b;

  border-color:
    #3b3c42;

  box-shadow:
    0 20px 46px
    rgba(
      0,
      0,
      0,
      0.34
    );
}

html[data-theme="dark"]
.dropdown-item:hover {
  background:
    rgba(
      67,
      135,
      238,
      0.08
    );
}

html[data-theme="dark"]
.dropdown-item.active {
  background:
    linear-gradient(
      135deg,
      rgba(
        53,
        132,
        247,
        0.14
      ),
      rgba(
        83,
        104,
        232,
        0.08
      )
    );
}

html[data-theme="dark"]
.item-dot {
  background: #50535a;
}

html[data-theme="dark"]
.dropdown-item:hover
.item-dot {
  background: #719fe8;
}

html[data-theme="dark"]
.dropdown-item.active
.item-dot {
  background: #5a91ee;
}

html[data-theme="dark"]
.item-name {
  color: #d9dbe0;
}

html[data-theme="dark"]
.dropdown-item.active
.item-name {
  color: #91b8fa;
}

/* =========================================================
   TRANSITION
========================================================= */

.dropdown-enter-active,
.dropdown-leave-active {
  transition:
    opacity
    0.18s ease,
    transform
    0.18s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;

  transform:
    translateY(-5px)
    scale(0.99);
}

/* =========================================================
   MOBILE
========================================================= */

@media (
  max-width: 640px
) {
  .dropdown-trigger {
    min-height: 54px;
  }

  .dropdown-list {
    max-height: 240px;
  }

  .selected-value {
    font-size: 12px;
  }
}

@media (
  prefers-reduced-motion:
  reduce
) {
  .dropdown-trigger,
  .arrow-box,
  .dropdown-item,
  .item-dot,
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: none;
  }
}
</style>